import { useMemo } from 'react';

import styled from 'styled-components';

import Star from '@components/common/Star';

import { FeedbackSummaryDto } from '@customTypes/Partner';

interface Props {
  reviewCount: number;
  reviewScores: FeedbackSummaryDto['scores'];
  reviewAvg: number;
}

type Rating = 1 | 2 | 3 | 4 | 5;

interface Stat {
  rating: Rating;
  count: number;
  ratio: number;
}

type Stats = Stat[];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 0px;
  background-color: #f3f4f8;
`;

const RatingWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const LeftWrapper = styled.div`
  display: flex;
  flex: 5;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-right: 1px solid #e3e5ea;
`;

const RatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 32px;
`;

const Grade = styled.div`
  margin-bottom: 8px;
`;

const RightWrapper = styled.div`
  display: flex;
  flex: 6;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  @media (min-width: 430px) {
    padding-left: 70px;
  }
`;

const Stat = styled.div`
  display: flex;
  min-width: 100%;
  font-size: 13px;
  &:first-child {
    color: #2e3035;
  }
  color: #a3a7ae;
`;

const RatingText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
`;

const Progress = styled.div`
  position: relative;
  flex: 1;
  max-width: 161px;
  margin-top: 6px;
  height: 6px;
  border-radius: 4px;
  background-color: #e3e5ea;
`;

const Percentage = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: #055cf5;
  border-radius: 4px;
`;

const Comment = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-left: 20px;
  padding: 16px 0 0;
  font-size: 13px;
  font-weight: 400;
  color: #a3a7ae;
`;

const PartnerFeedbackSummary: React.FC<Props> = (props) => {
  const { reviewScores, reviewAvg } = props;

  const stats = useMemo(() => {
    const result: Stats = [];
    const total = Object.values(reviewScores).reduce((sum, count) => sum + count, 0);

    for (let i = 5; i > 0; i--) {
      result.push({
        rating: i as Rating,
        count: reviewScores[i as Rating],
        ratio: reviewScores[i as Rating] / total,
      });
    }

    return result;
  }, [reviewScores]);

  return (
    <Wrapper>
      <RatingWrapper>
        <LeftWrapper>
          <RatingContainer>
            <Grade>{reviewAvg.toFixed(2)}</Grade>
            <Star height={24} rating={Number(reviewAvg.toFixed(1))} />
          </RatingContainer>
        </LeftWrapper>
        <RightWrapper>
          {stats.map(({ rating, count, ratio }) => (
            <Stat key={rating}>
              <RatingText>{rating}점</RatingText>
              <Progress>
                <Percentage style={{ width: `${ratio * 100}%` }} />
              </Progress>
              <RatingText>{count.toLocaleString('ko-KR')}</RatingText>
            </Stat>
          ))}
        </RightWrapper>
      </RatingWrapper>
      <Comment>*실제 미소에서 서비스를 받은 이용자들의 후기입니다.</Comment>
    </Wrapper>
  );
};

export default PartnerFeedbackSummary;
